import type { TaskCompletion, FamilyMember } from '@/types'
import type { Achievement, UnlockCriteria } from '@/types/achievements'
import { startOfDay, isWeekend, getHours } from 'date-fns'

// Check if a user meets the criteria for an achievement
export function checkAchievementCriteria(
  criteria: UnlockCriteria,
  userData: {
    taskCompletions: TaskCompletion[]
    member: FamilyMember
    supplies?: number
  }
): { met: boolean; progress: number; target: number } {
  const { taskCompletions, member } = userData

  switch (criteria.type) {
    case 'task_count': {
      const count = taskCompletions.length
      return {
        met: count >= criteria.value,
        progress: count,
        target: criteria.value,
      }
    }

    case 'daily_task_count': {
      const today = startOfDay(new Date())
      const todayCompletions = taskCompletions.filter((c) => {
        const completedDate = startOfDay(new Date(c.completed_at))
        return completedDate.getTime() === today.getTime()
      })
      return {
        met: todayCompletions.length >= criteria.value,
        progress: todayCompletions.length,
        target: criteria.value,
      }
    }

    case 'streak': {
      const streak = member.streak
      return {
        met: streak >= criteria.value,
        progress: streak,
        target: criteria.value,
      }
    }

    case 'fast_completion': {
      const fastCompletions = taskCompletions.filter((c) => {
        // A completion is "fast" if time_taken < estimated_duration
        return c.time_taken && c.task?.estimated_duration && c.time_taken < c.task.estimated_duration
      })
      return {
        met: fastCompletions.length >= criteria.value,
        progress: fastCompletions.length,
        target: criteria.value,
      }
    }

    case 'perfect_week': {
      // Check if all tasks were completed for the last 7 days
      // This is a simplified check - in production you'd want more sophisticated logic
      const sevenDaysAgo = new Date()
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)

      const recentCompletions = taskCompletions.filter((c) =>
        new Date(c.completed_at) >= sevenDaysAgo
      )

      // Rough approximation - should check against assigned tasks
      const perfectWeek = recentCompletions.length >= criteria.value * 7
      return {
        met: perfectWeek,
        progress: recentCompletions.length,
        target: criteria.value * 7,
      }
    }

    case 'on_time': {
      const onTimeCompletions = taskCompletions.filter((c) => c.on_time)
      return {
        met: onTimeCompletions.length >= criteria.value,
        progress: onTimeCompletions.length,
        target: criteria.value,
      }
    }

    case 'task_type': {
      const typeCompletions = taskCompletions.filter(
        (c) => c.task?.task_type === criteria.task_type
      )
      return {
        met: typeCompletions.length >= criteria.value,
        progress: typeCompletions.length,
        target: criteria.value,
      }
    }

    case 'inventory_add': {
      const suppliesCount = userData.supplies || 0
      return {
        met: suppliesCount >= criteria.value,
        progress: suppliesCount,
        target: criteria.value,
      }
    }

    case 'inventory_vigilance': {
      // This would need more sophisticated tracking in production
      return {
        met: false,
        progress: 0,
        target: criteria.value,
      }
    }

    case 'early_completion': {
      const earlyCompletions = taskCompletions.filter((c) => {
        const hour = getHours(new Date(c.completed_at))
        return hour < (criteria.hour || 8)
      })
      return {
        met: earlyCompletions.length > 0,
        progress: earlyCompletions.length,
        target: 1,
      }
    }

    case 'late_completion': {
      const lateCompletions = taskCompletions.filter((c) => {
        const hour = getHours(new Date(c.completed_at))
        return hour >= (criteria.hour || 22)
      })
      return {
        met: lateCompletions.length > 0,
        progress: lateCompletions.length,
        target: 1,
      }
    }

    case 'weekend_tasks': {
      const weekendCompletions = taskCompletions.filter((c) =>
        isWeekend(new Date(c.completed_at))
      )
      return {
        met: weekendCompletions.length >= (criteria.value || 5),
        progress: weekendCompletions.length,
        target: criteria.value || 5,
      }
    }

    default:
      return { met: false, progress: 0, target: 0 }
  }
}

// Calculate user level based on points
export function calculateLevel(points: number): { level: number; progress: number; nextLevelPoints: number } {
  // Level formula: Each level requires progressively more points
  // Level 1: 0-99 points
  // Level 2: 100-299 points
  // Level 3: 300-599 points
  // Level n: Uses quadratic scaling

  let level = 1
  let totalNeeded = 0

  while (totalNeeded <= points) {
    const pointsForNextLevel = level * 100 + (level - 1) * 50
    totalNeeded += pointsForNextLevel
    if (totalNeeded <= points) {
      level++
    }
  }

  // Calculate progress to next level
  const pointsForNextLevel = level * 100 + (level - 1) * 50
  const pointsIntoCurrentLevel = points - (totalNeeded - pointsForNextLevel)
  const progress = (pointsIntoCurrentLevel / pointsForNextLevel) * 100

  return {
    level,
    progress: Math.max(0, Math.min(100, progress)),
    nextLevelPoints: pointsForNextLevel - pointsIntoCurrentLevel,
  }
}

// Get achievement tier display properties
export function getAchievementTierProps(tier: Achievement['tier']) {
  const tierProps = {
    Bronze: {
      color: '#cd7f32',
      gradient: 'linear-gradient(135deg, #cd7f32 0%, #b87333 100%)',
      emoji: '🥉',
    },
    Silver: {
      color: '#c0c0c0',
      gradient: 'linear-gradient(135deg, #c0c0c0 0%, #a8a8a8 100%)',
      emoji: '🥈',
    },
    Gold: {
      color: '#ffd700',
      gradient: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
      emoji: '🥇',
    },
    Platinum: {
      color: '#e5e4e2',
      gradient: 'linear-gradient(135deg, #e5e4e2 0%, #b8b8b8 100%)',
      emoji: '💎',
    },
  }

  return tierProps[tier]
}

// Format achievement progress as percentage
export function getAchievementProgressPercentage(current: number, target: number): number {
  if (target === 0) return 0
  return Math.min(100, (current / target) * 100)
}
